// Hardcoded sample MurphyReport for Phase-1 layout verification. Tuned to
// spec §13 word-count targets so the rendered output mirrors
// docs/murphy-report/Murphy_Report_Mockup.pdf.
//
// All field names match the spec's `MurphyReport` type (spec §12).
const sampleData = {
  variant: 'internal',
  meeting: {
    title: 'Acme Corp · Demo with platform team',
    dateISO: '2026-05-12T14:00:00Z',
    durationMinutes: 47,
    type: 'Demo',
    dealStage: 'Mid',
    sentiment: 'Positive',
    participants: [
      { name: 'Ryan Chen', role: 'Account Executive', company: 'Multiply', isHost: true },
      { name: 'Sarah Martinez', role: 'VP Engineering', company: 'Acme', isHost: false },
      { name: 'David Liu', role: 'Director of Platform', company: 'Acme', isHost: false },
      { name: 'Priya Shah', role: 'Senior Engineer', company: 'Acme', isHost: false },
      { name: 'Mark Reynolds', role: 'Procurement', company: 'Acme', isHost: false },
    ],
  },
  murphyReview: {
    status: 'not-delivered',
    statusColor: 'red',
    statusLabel: 'Pitch not delivered',
    pitchType: 'Who We Are',
    pitchSubtitle: 'the casual-encounter opener',
    score: 32,
    headline:
      "Ryan ran a strong demo and answered every product question, but the Acme team still hasn't heard Multiply's core story. Bring the Who We Are pitch into the next conversation before any pricing follow-up.",
    covered: [
      'Spoke in a natural, conversational tone throughout the demo and kept the room engaged for the full 47 minutes.',
      'Surfaced the platform-team workflow problem with a clear example and tied it directly to the Multiply demo flow.',
      'Handled the procurement objection on data residency with specifics about region pinning and the audit log.',
      'Confirmed Sarah is the economic buyer and Mark from procurement is the gate, with a follow-up scheduled for next week.',
    ],
    missing: [
      'Did not deliver the Who We Are opener — the company story, what makes Multiply different, why now.',
      'Skipped the founder anecdote that anchors the discovery framework in a real sales-team failure mode.',
      'Closed without naming the broader problem space Multiply addresses beyond the immediate platform use case.',
      'Left the differentiated value proposition implicit; Acme will likely compare Multiply against generic alternatives.',
    ],
    coachingTips: [
      'Practice the Casual Encounter pitch in Training Hub before the next Acme touchpoint — focus on the 90-second version.',
      'Open the follow-up with the founder anecdote, then bridge into the platform-team use case Sarah surfaced today.',
      'Send a recap email that names the broader problem Multiply solves, not just the demo feature set.',
    ],
  },
  summary:
    "Ryan led a 47-minute demo for Acme's platform team. Sarah (VP Engineering) confirmed budget for Q3 and pulled Mark from procurement into the conversation. Acme has a clear platform-team workflow problem that maps to Multiply's discovery framework, but the broader company story was not delivered.",
  alignment: {
    fitLabel: 'Strong fit',
    fitColor: 'green',
    lead:
      "Acme's platform team is hitting three problems Multiply solves directly. Sarah surfaced each one without prompting — explicit fit signals.",
    matches: [
      {
        title: 'Platform team is rebuilding sales-team workflows from scratch each quarter',
        quotes: [{ text: "Every quarter we end up redoing the integration work because the sales team's process changes faster than our codebase can keep up. It's bleeding two engineers continuously.", attribution: 'Sarah Martinez', evidence: 'Explicit' }],
        offerings: ['Discovery framework', 'Workflow capture'],
      },
      {
        title: 'No central source of truth for sales pitches across the org',
        quotes: [{ text: "Reps are improvising. Half of them have their own version of the elevator pitch and we can't tell which one is closing deals.", attribution: 'Sarah Martinez', evidence: 'Explicit' }],
        offerings: ['Pitch library', 'Murphy grading'],
      },
      {
        title: 'Onboarding new reps takes 90+ days before they hit quota',
        quotes: [{ text: 'We just brought on six SDRs and the ramp time is killing us. By the time they know the product, half are gone.', attribution: 'David Liu', evidence: 'Implied' }],
        offerings: ['Onboarding pathways'],
      },
    ],
  },
  moments: [
    {
      timestamp: '04:12',
      type: 'Opportunity',
      description: 'Sarah opened with the workflow rebuild problem before Ryan asked any discovery questions.',
      whyItMatters: 'Inbound problem signal — Acme is already framing this as urgent.',
    },
    {
      timestamp: '18:30',
      type: 'Concern',
      description: 'Mark from procurement raised data residency for EU customers and asked about region pinning.',
      whyItMatters: 'Procurement is engaged early — they will be the gate on contract terms.',
    },
    {
      timestamp: '27:45',
      type: 'Commitment',
      description: "Sarah agreed to bring David into the next call to scope the integration work.",
      whyItMatters: 'David owns the platform — his sign-off is required before any rollout.',
    },
    {
      timestamp: '38:02',
      type: 'Objection',
      description: 'Mark pushed back on the per-seat pricing model — wants enterprise flat rate.',
      whyItMatters: 'Pricing is now the open thread; need to come with a structured offer.',
    },
    {
      timestamp: '44:18',
      type: 'Decision',
      description: 'Acme team committed to a 30-day pilot if pricing lands within their range.',
      whyItMatters: 'Pilot is the unlock — pricing structure is the only remaining variable.',
    },
  ],
  opportunities: [
    {
      type: 'Expansion',
      description: 'Sarah hinted at the customer-success team having the same workflow problem as platform.',
      recommendation: 'Ask in the next call whether CS would be in scope for the pilot — extends ACV by ~40%.',
      whyWeFit: 'Multiply already serves CS use cases in three other accounts of similar size.',
    },
    {
      type: 'Referral',
      description: 'David mentioned two peer companies hitting the same onboarding ramp-time problem.',
      recommendation: 'Ask David for a warm intro to one of them after the pilot kicks off.',
      whyWeFit: 'Multiply has case studies in adjacent verticals that map directly to the named accounts.',
    },
    {
      type: 'Cross-sell',
      description: 'Acme is on a competing pitch-library tool that comes up for renewal in 4 months.',
      recommendation: 'Position Multiply as a replacement bundle, not an add-on, when pricing the pilot.',
      whyWeFit: 'Murphy + pitch library covers the competitor scope plus the grading layer they lack.',
    },
  ],
  risks: [
    {
      type: 'Pricing',
      severity: 'high',
      description: 'Mark from procurement explicitly wants enterprise flat rate, not per-seat — incompatible with current price book.',
      mitigation: 'Bring a flat-rate proposal to the next call. Loop in pricing committee this week.',
    },
    {
      type: 'Champion',
      severity: 'medium',
      description: 'Sarah is the buyer but does not own the platform team — David could veto if integration scope grows.',
      mitigation: 'Schedule a separate scoping call with David before the pilot SOW lands on Sarah’s desk.',
    },
    {
      type: 'Competitive',
      severity: 'low',
      description: 'Existing pitch-library vendor renews in 4 months — likely to come back with a discount.',
      mitigation: 'Lock in pilot terms before the renewal window opens to set anchor pricing.',
    },
  ],
  nextSteps: [
    {
      action: 'Send Sarah the platform-team case study and a flat-rate pricing draft',
      owner: 'Ryan',
      when: 'This week',
      priority: 'high',
    },
    {
      action: 'Schedule scoping call with David Liu on integration surface area',
      owner: 'Ryan',
      when: 'Next week',
      priority: 'high',
    },
    {
      action: 'Loop pricing committee on enterprise flat-rate structure',
      owner: 'Ryan + Pricing',
      when: 'By Fri',
      priority: 'medium',
    },
    {
      action: 'Practice Casual Encounter pitch in Training Hub before next Acme call',
      owner: 'Ryan',
      when: 'Before follow-up',
      priority: 'high',
    },
    {
      action: 'Draft a recap email that names the broader problem space',
      owner: 'Ryan',
      when: 'Today',
      priority: 'medium',
    },
  ],
  salesRepPerformance: {
    overallAssessment:
      'Ryan ran a controlled, well-paced demo that surfaced clear fit signals and handled procurement concerns confidently. The gap is the company-message frame at the open — bring it back in for the follow-up.',
    strengths: [
      'Held the room for the full 47 minutes with a conversational tone',
      'Tied each product feature directly to a problem Sarah surfaced',
      'Answered the data residency objection from procurement with specifics',
      'Confirmed economic buyer and procurement gate in the same call',
    ],
    areasForImprovement: [
      'Skipped the Who We Are opener that anchors every Multiply conversation',
      'Did not name the broader problem space Multiply addresses',
      'Left the differentiated value proposition implicit',
      'Deflected on pricing rather than offering a structured response',
    ],
    coachingTips: [
      'Open the follow-up with the Casual Encounter pitch — practice the 90-second version in Training Hub',
      'Lead with the founder anecdote, then bridge into the platform-team use case Sarah surfaced',
      'Bring a structured pricing proposal (seat tiers + enterprise flat-rate) to the next call',
      'Send a recap email that names the broader problem Multiply solves',
    ],
  },
  profile: {
    painPoints: [
      'Two engineers continuously rebuilding sales workflow integrations',
      'No central source of truth for sales pitches across the org',
      '90+ day ramp time for new SDRs',
      'EU data residency requirements not met by current tooling',
    ],
    buyingSignals: [
      'Budget confirmed for Q3 by Sarah',
      'Procurement engaged in the first demo call',
      '30-day pilot commitment subject to pricing',
      'Peer companies identified for post-pilot referral',
    ],
    decisionCriteria: [
      'Enterprise flat-rate pricing',
      'Region pinning for EU customer data',
      'Integration scope under 4 weeks',
      'Demonstrated ramp-time reduction',
    ],
    targetMarketFit: {
      level: 'Excellent',
      color: 'green',
      reasoning: 'Mid-stage B2B SaaS with engineering and sales scale problems. Multiply has three reference accounts at similar size in the same vertical with measurable ramp-time and pitch-consistency improvements.',
    },
  },
  qa: [
    {
      question: 'How does Multiply integrate with our existing CRM and what does the engineering lift look like?',
      expectedAnswer: 'Native integrations for Salesforce, HubSpot, and Pipedrive. Engineering lift is typically 2-4 weeks for a mid-size org with a dedicated platform engineer, including custom field mapping and pipeline event subscriptions.',
      completeness: 78,
      accuracy: 85,
      coachingTip: 'Ryan covered the timeline well but did not surface the case study from Wayfinder which would have neutralized David’s integration concern.',
      pointsCovered: ['Integration timeline', 'CRM coverage'],
      pointsMissed: ['Specific case study', 'Custom field mapping example'],
    },
    {
      question: 'Can you walk us through how Murphy grades a call and what the rep sees afterward?',
      expectedAnswer: 'Murphy uses Claude Sonnet 4.0 to grade against company-specific rubrics stored in S3. Reps see a per-criterion score, positives, areas for improvement, and a coaching banner if the core pitch was not delivered. Graders can drill into specific question responses with timestamps.',
      completeness: 92,
      accuracy: 90,
      coachingTip: 'Strong answer — keep using this depth in technical demos. Worth showing an actual graded call on screen next time.',
      pointsCovered: ['Grading model', 'Rep experience', 'Rubric source'],
      pointsMissed: [],
    },
    {
      question: 'What does pricing look like for an org our size?',
      expectedAnswer: 'For mid-market we typically price per seat with volume discounts at 50, 100, and 250 seats. Enterprise flat-rate is available for orgs above 250 seats or with predictable expansion plans. Annual contracts include onboarding, success management, and quarterly business reviews.',
      completeness: 45,
      accuracy: 60,
      coachingTip: 'Ryan deflected on pricing rather than offering a structured response. Next time: lead with the seat tiers, then position the enterprise flat-rate as the path forward given the procurement signal.',
      pointsCovered: ['Mention of pricing structure'],
      pointsMissed: ['Seat tiers', 'Enterprise option', 'What’s included annually'],
    },
  ],
};

export default sampleData;
